/**
 * F44 (Wave 2D) — admin finance dashboard home.
 *
 * Route: `/finance`. Permission: `financial:read` (finance + super_admin).
 *
 * Layout: quick-preset Chip row + DateRangePicker (custom) + compare-to-previous
 * Switch → fetch GET /admin/financial/summary → 12 KPI Stat cards (+ admin_credits)
 * with PercentDelta indicators when compare is on; charts strip (Line revenue-over-
 * time / Bar revenue-by-source / Donut revenue-mix / Funnel issued→settled→overdue
 * / AgingBar from GET /admin/financial/receivables) + Top-N strip (3 tables) + a
 * "Last refreshed at HH:MM" Banner with a manual-refresh Button + a persistent
 * "Pre-tax" Banner.
 *
 * The rollup-backed KPIs serve last-hour's snapshot; the banner makes that
 * explicit; the refresh Button updates `lastRefreshedAt` (rate-limited 1/5min,
 * server-enforced — a too-frequent retap surfaces a Toast).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chart,
  Chip,
  CurrencyDisplay,
  DateRangePicker,
  EmptyState,
  Skeleton,
  Stat,
  Switch,
  Table,
  useToast,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import type {
  ArAgingBucket,
  FinanceKpiCard,
  FinanceReceivables,
  FinanceSummary,
  FinanceTopRow,
} from '@bravemove/types';
import { ApiError } from '../../auth/types';
import { adminFinancialApi } from '../../api/financial';
import { PageHeader } from '../../components/PageHeader';
import { AgingBar } from '../../components/finance/AgingBar';
import { PercentDelta } from '../../components/finance/PercentDelta';
import {
  defaultFinanceRange,
  deltaTone,
  resolvePreset,
  formatRefreshTime,
  type FinanceQuickPreset,
  type FinanceRangeValue,
} from '../../components/finance/financeHelpers';
import { FinanceNav } from './FinanceNav';

const QUICK_PRESETS: ReadonlyArray<{ value: FinanceQuickPreset; labelKey: string }> = [
  { value: 'today', labelKey: 'admin:financial.dashboard.quick.today' },
  { value: 'thisWeek', labelKey: 'admin:financial.dashboard.quick.thisWeek' },
  { value: 'thisMonth', labelKey: 'admin:financial.dashboard.quick.thisMonth' },
  { value: 'lastMonth', labelKey: 'admin:financial.dashboard.quick.lastMonth' },
  { value: 'ytd', labelKey: 'admin:financial.dashboard.quick.ytd' },
  { value: 'custom', labelKey: 'admin:financial.dashboard.quick.custom' },
];

export function FinanceDashboardPage() {
  const { t, locale } = useI18n();
  const { push } = useToast();

  const [preset, setPreset] = useState<FinanceQuickPreset>('thisMonth');
  const [range, setRange] = useState<FinanceRangeValue>(() => defaultFinanceRange());
  const [compare, setCompare] = useState(false);

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [receivables, setReceivables] = useState<FinanceReceivables | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        adminFinancialApi.getSummary({ from: range.from, to: range.to, compare }),
        adminFinancialApi.getReceivables(),
      ]);
      setSummary(s);
      setReceivables(r);
    } catch {
      setError(t('admin:financial.dashboard.rangeError'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, compare, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchAll();
  }, [fetchAll]);

  const onPreset = (value: FinanceQuickPreset) => {
    setPreset(value);
    const resolved = resolvePreset(value);
    if (resolved) setRange(resolved);
  };

  const onCustomRange = (dr: DateRange) => {
    setPreset('custom');
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await adminFinancialApi.refresh();
      push({ tone: 'success', title: t('admin:financial.dashboard.refreshed') });
      await fetchAll();
    } catch (err) {
      const apiErr = err as { status?: number; code?: string } | null;
      const status = err instanceof ApiError ? err.status : apiErr?.status;
      const code = err instanceof ApiError ? err.code : apiErr?.code;
      // The 1/5min limiter's 429 body carries the GLOBAL @fastify/rate-limit
      // plugin code RATE_LIMIT_EXCEEDED — no FINANCE_-specific code is ever
      // emitted by the server (the refresh-429-code-mismatch audit fix). The
      // legacy code is kept defensively in case a route-level builder lands.
      if (
        status === 429 ||
        code === 'RATE_LIMIT_EXCEEDED' ||
        code === 'FINANCE_REFRESH_RATE_LIMITED'
      ) {
        push({ tone: 'warning', title: t('admin:financial.dashboard.refreshRateLimited') });
      } else {
        push({ tone: 'danger', title: t('admin:financial.dashboard.rangeError') });
      }
    } finally {
      setRefreshing(false);
    }
  };

  const refreshLabel = formatRefreshTime(summary?.lastRefreshedAt ?? null, locale);

  return (
    <div className="space-y-6" data-testid="finance-dashboard-page">
      <PageHeader
        eyebrow="Finance"
        title={t('admin:financial.dashboard.title')}
        subtitle={t('admin:financial.dashboard.subtitle')}
      />

      <FinanceNav />

      {/* Persistent pre-tax disclaimer */}
      <div data-testid="finance-pretax-banner">
        <Banner tone="info">{t('admin:financial.dashboard.preTaxBanner')}</Banner>
      </div>

      {/* Last-refreshed + manual refresh */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hairline bg-surface px-4 py-3"
        data-testid="finance-refresh-bar"
      >
        <span className="text-[12.5px] text-ink-2">
          {refreshLabel
            ? t('admin:financial.dashboard.lastRefreshed', { time: refreshLabel })
            : t('admin:financial.dashboard.lastRefreshedNever')}
        </span>
        <Button
          variant="secondary"
          size="sm"
          loading={refreshing}
          onClick={onRefresh}
          data-testid="finance-refresh-button"
        >
          {refreshing
            ? t('admin:financial.dashboard.refreshing')
            : t('admin:financial.dashboard.refresh')}
        </Button>
      </div>

      {/* Period controls */}
      <div className="flex flex-col gap-3" data-testid="finance-period-controls">
        <div className="flex flex-wrap items-center gap-2">
          {QUICK_PRESETS.map((p) => (
            <Chip
              key={p.value}
              selected={preset === p.value}
              onClick={() => onPreset(p.value)}
              data-testid={`finance-preset-${p.value}`}
            >
              {t(p.labelKey)}
            </Chip>
          ))}
          <Switch
            label={t('admin:financial.dashboard.compareToggle')}
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            data-testid="finance-compare-toggle"
          />
        </div>
        {preset === 'custom' ? (
          <DateRangePicker
            value={{ start: range.from, end: range.to }}
            onChange={onCustomRange}
            locale={locale}
          />
        ) : null}
      </div>

      {error ? (
        <div data-testid="finance-error">
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {loading || !summary ? (
        <div
          className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
          data-testid="finance-loading"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div
            className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
            data-testid="finance-kpi-grid"
          >
            {summary.kpis.map((card) => (
              <KpiCard key={card.key} card={card} locale={locale} compare={compare} />
            ))}
          </div>

          {/* Charts strip */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t('admin:financial.dashboard.charts.revenueOverTime')}>
              <Chart
                kind="line"
                data={summary.trend.map((p) => ({
                  day: p.day,
                  gross: p.grossMinor / 100,
                  net: p.netMinor / 100,
                }))}
                xKey="day"
                showLegend
                series={[
                  { key: 'gross', label: t('admin:financial.dashboard.series.gross') },
                  { key: 'net', label: t('admin:financial.dashboard.series.net') },
                ]}
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:financial.dashboard.charts.revenueBySource')}>
              <Chart
                kind="bar"
                data={summary.bySource.map((s) => ({
                  source: t(`commission:source.${s.source}`, { defaultValue: s.source }),
                  net: s.netMinor / 100,
                }))}
                xKey="source"
                series={[{ key: 'net', label: t('admin:financial.dashboard.series.net') }]}
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:financial.dashboard.charts.revenueMix')}>
              <Chart
                kind="donut"
                data={summary.bySource.map((s) => ({
                  source: t(`commission:source.${s.source}`, { defaultValue: s.source }),
                  value: s.netMinor / 100,
                }))}
                xKey="source"
                series={[{ key: 'value' }]}
                showLegend
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:financial.dashboard.charts.settlementFunnel')}>
              <Chart
                kind="funnel"
                data={[
                  {
                    stage: t('admin:financial.dashboard.funnel.issued'),
                    value: summary.funnel.issuedMinor / 100,
                  },
                  {
                    stage: t('admin:financial.dashboard.funnel.settled'),
                    value: summary.funnel.settledMinor / 100,
                  },
                  {
                    stage: t('admin:financial.dashboard.funnel.overdue'),
                    value: summary.funnel.overdueMinor / 100,
                  },
                ]}
                xKey="stage"
                series={[{ key: 'value' }]}
                height={240}
              />
            </ChartCard>
          </div>

          {/* AR aging */}
          <ChartCard title={t('admin:financial.dashboard.charts.arAging')}>
            {receivables ? (
              <AgingBar
                segments={receivables.buckets}
                bucketLabel={(b) => t(`admin:financial.aging.${b}`)}
                ariaLabel={t('admin:financial.dashboard.charts.arAging')}
                locale={locale}
              />
            ) : (
              <Skeleton className="h-12 w-full rounded-lg" />
            )}
          </ChartCard>

          {/* Top-N strip */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <TopTable
              title={t('admin:financial.dashboard.topVenues')}
              rows={summary.topVenues}
              locale={locale}
              emptyLabel={t('admin:financial.dashboard.topEmpty')}
              testId="finance-top-venues"
            />
            <TopTable
              title={t('admin:financial.dashboard.topTrainers')}
              rows={summary.topTrainers}
              locale={locale}
              emptyLabel={t('admin:financial.dashboard.topEmpty')}
              testId="finance-top-trainers"
            />
            <TopTable
              title={t('admin:financial.dashboard.topTournaments')}
              rows={summary.topTournaments}
              locale={locale}
              emptyLabel={t('admin:financial.dashboard.topEmpty')}
              testId="finance-top-tournaments"
            />
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({
  card,
  locale,
  compare,
}: {
  card: FinanceKpiCard;
  locale: string;
  compare: boolean;
}) {
  const { t } = useI18n();
  const showDelta = compare && card.deltaPercent !== undefined;
  const delta: ReactNode = showDelta ? (
    <PercentDelta deltaPercent={card.deltaPercent!} label={t('admin:financial.kpi.vsPrevious')} />
  ) : card.informationalOnly ? (
    t('admin:financial.kpi.informational')
  ) : null;
  return (
    <div data-testid={`finance-kpi-${card.key}`} data-informational={card.informationalOnly}>
      <Stat
        {...(card.informationalOnly ? { className: 'opacity-90 border-dashed' } : {})}
        label={t(`admin:financial.kpi.${card.key}`)}
        value={
          <CurrencyDisplay
            amount={card.amountMinor}
            currency={card.currency}
            locale={locale}
            size="lg"
          />
        }
        {...(delta ? { delta } : {})}
        deltaTone={showDelta ? deltaTone(card.deltaPercent!) : 'neutral'}
      />
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-hairline bg-surface p-4">
      <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

function TopTable({
  title,
  rows,
  locale,
  emptyLabel,
  testId,
}: {
  title: string;
  rows: readonly FinanceTopRow[];
  locale: string;
  emptyLabel: string;
  testId: string;
}) {
  const { t } = useI18n();
  const columns = useMemo<readonly TableColumn<FinanceTopRow>[]>(
    () => [
      {
        key: 'name',
        header: title,
        cell: (row) => (
          <Link
            to={row.deepLinkPath}
            className="text-[13px] font-semibold text-ink hover:underline"
            data-testid={`${testId}-link-${row.entityId}`}
          >
            {row.entityName}
          </Link>
        ),
      },
      {
        key: 'net',
        header: t('admin:financial.kpi.net_revenue'),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.netMinor} currency="USD" locale={locale} size="sm" />
        ),
      },
    ],
    [title, locale, t, testId],
  );
  return (
    <div data-testid={testId}>
      {rows.length === 0 ? (
        <section className="rounded-xl border border-hairline bg-surface p-4">
          <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
            {title}
          </h2>
          <EmptyState title={emptyLabel} />
        </section>
      ) : (
        <Table
          data={rows as FinanceTopRow[]}
          columns={columns as TableColumn<FinanceTopRow>[]}
          rowKey={(row) => `${row.entityKind}:${row.entityId}`}
          density="compact"
        />
      )}
    </div>
  );
}

// Re-export the aging bucket type so tests that import labels stay typed.
export type { ArAgingBucket };

export default FinanceDashboardPage;
